import type {
  Auth,
  SerializedToken,
  Token,
} from '@integration-layer/configs/commercelayer'
import { isTokenValid } from '@integration-layer/configs/commercelayer'
import { StorageSerializers } from '@vueuse/core'

/**
 * Composable for managing Commerce Layer guest authentication.
 *
 * This composable handles token retrieval, storage, and refresh for guest users
 * in a Commerce Layer integration. It supports:
 * - In-memory token caching
 * - Local storage token persistence
 * - Token validation and automatic refresh
 *
 * @returns {Auth} An authentication object with organization and token management methods.
 *
 * @example
 * const { organization, getToken, refreshToken } = useClAuth()
 *
 * // Get a valid access token
 * const accessToken = await getToken()
 */
export const useClAuth = (): Auth => {
  const config = useRuntimeConfig()
  const configs = useConfigs()
  const organization = config.public.commercelayer.organization
  const market = configs.value.cl_market_code!

  /**
   * In-memory cache for token promises per market.
   * Prevents multiple simultaneous token refresh requests.
   *
   * @type {Record<string, Promise<SerializedToken>>}
   * @private
   */
  const inMemoryTokenPromisePerMarket: Record<
    string,
    Promise<SerializedToken>
  > = {}

  /**
   * Local storage for persisting guest tokens.
   * Only initialized on the client-side to avoid SSR issues.
   *
   * @type {import('@vueuse/core').Ref<SerializedToken | null>}
   * @private
   */
  const storage = process.client
    ? useLocalStorage<SerializedToken>(
        `commercelayer:guestToken:${market}`,
        null,
        {
          serializer: StorageSerializers.object,
        }
      )
    : null

  /**
   * Retrieves a valid access token.
   *
   * Checks for an existing valid token in memory or local storage.
   * If no valid token exists, it triggers a token refresh.
   *
   * @async
   * @returns {Promise<string>} A valid access token.
   * @throws {Error} If token retrieval or refresh fails.
   */
  const getToken = async () => {
    const tokenPromise = inMemoryTokenPromisePerMarket[market]
    const token = (await tokenPromise) ?? storage?.value

    if (token && isTokenValid(token)) return token.accessToken

    return await refreshToken()
  }

  /**
   * Refreshes the access token for the current market.
   *
   * Fetches a new token from the server and updates:
   * - In-memory token promise cache
   * - Local storage (if available)
   *
   * @async
   * @returns {Promise<string>} A new access token.
   * @throws {Error} If token refresh fails.
   */
  const refreshToken = async () => {
    const tokenPromise = $fetch(`/api/getCLGuestToken/${market}`)
    inMemoryTokenPromisePerMarket[market] = tokenPromise
    const newToken = await tokenPromise

    if (storage) {
      storage.value = newToken
    }

    return newToken.accessToken
  }

  return {
    organization,
    getToken,
    refreshToken,
  }
}
